import { useMutation, useQuery } from "react-query";
import { useAppContext } from "../AppProviders";
import { API_URL } from "./helpers";
import {
  CloneReportDocumentsRequest,
  GetItemDocumentUploadUrlRequest,
  GetPersonDocumentUploadUrlRequest,
  GetPersonExtractDocumentUploadUrlRequest,
  ItemDocument,
  PersonConsentForm,
  PersonDocument,
  ExtractDocumentInfoRequest,
  PresignedS3UploadUrl,
  SanitizeItemDocumentRequest,
  SanitizeItemDocumentResponse,
} from "./360Files";

export function useGetPersonDocumentUploadUrl() {
  const { requestApi } = useAppContext();

  const getPersonDocumentUploadUrl = async (
    input: GetPersonDocumentUploadUrlRequest
  ): Promise<PresignedS3UploadUrl> => {
    const { person_uuid, document_type, filename } = input;
    const res = await requestApi.post(
      `${API_URL}/person/${person_uuid}/documents/upload-url?document_type=${document_type}&filename=${encodeURIComponent(
        filename
      )}`
    );
    return res.data;
  };

  const mutationResult = useMutation(
    (input: GetPersonDocumentUploadUrlRequest) =>
      getPersonDocumentUploadUrl(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetPersonExtractDocumentUploadUrl() {
  const { requestApi } = useAppContext();

  const getPersonExtractDocumentUploadUrl = async (
    input: GetPersonExtractDocumentUploadUrlRequest
  ): Promise<PresignedS3UploadUrl> => {
    const { person_uuid, item_type, filename } = input;
    const res = await requestApi.post(
      `${API_URL}/person/${person_uuid}/documents/extract/upload-url?item_type=${item_type}&filename=${encodeURIComponent(
        filename
      )}`
    );
    return res.data;
  };

  const mutationResult = useMutation(
    (input: GetPersonExtractDocumentUploadUrlRequest) =>
      getPersonExtractDocumentUploadUrl(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetItemDocumentUploadUrl() {
  const { requestApi } = useAppContext();

  const getItemDocumentUploadUrl = async (
    input: GetItemDocumentUploadUrlRequest
  ): Promise<PresignedS3UploadUrl> => {
    const { item_uuid, filename } = input;
    const res = await requestApi.post(
      `${API_URL}/item/${item_uuid}/documents/upload-url?filename=${encodeURIComponent(
        filename
      )}`
    );
    return res.data;
  };

  const mutationResult = useMutation(
    (input: GetItemDocumentUploadUrlRequest) => getItemDocumentUploadUrl(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useSanitizeItemDocument() {
  const { requestApi } = useAppContext();

  const sanitizeItemDocument = async (
    input: SanitizeItemDocumentRequest
  ): Promise<SanitizeItemDocumentResponse> => {
    const { item_uuid, object_key } = input;
    const params = new URLSearchParams();
    params.append("object_key", object_key);
    const res = await requestApi.post(
      `${API_URL}/item/${item_uuid}/documents/sanitize?${params.toString()}`
    );
    return res.data;
  };

  const mutationResult = useMutation(
    (input: SanitizeItemDocumentRequest) => sanitizeItemDocument(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUploadFileToS3() {
  const uploadFileToS3 = async ({
    uploadUrl,
    file,
  }: {
    uploadUrl: string;
    file: File;
  }): Promise<void> => {
    const res = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
    });
    if (!res.ok) throw new Error("Failed to upload file to S3");
  };

  const mutationResult = useMutation(uploadFileToS3, {});

  return {
    ...mutationResult,
  };
}

export interface GetDocsArgs {
  personUuid: string;
  document_type?: string;
  is_attachment?: boolean;
}

export function useGetPersonConsentForms(personUuid: string) {
  const { requestApi } = useAppContext();
  const getPersonConsentForms = async (): Promise<PersonConsentForm[]> => {
    if (!personUuid) return [];
    const res = await requestApi.get(
      `${API_URL}/person/${personUuid}/consent_forms`
    );
    return res.data ?? [];
  };
  return useQuery(["personConsentForms", personUuid], getPersonConsentForms, {
    enabled: !!personUuid,
  });
}

export function useGetPersonDocuments() {
  const { requestApi } = useAppContext();
  const getPersonDocuments = async (
    personUuid: string,
    document_type?: string,
    is_attachment?: boolean
  ): Promise<PersonDocument[]> => {
    let url = `${API_URL}/person/${personUuid}/documents`;
    if (document_type) {
      url = `${url}?document_type=${document_type}`;
    }
    if (is_attachment) {
      url = `${url}${url.includes("?") ? "&" : "?"}is_attachment=true`;
    }
    const res = await requestApi.get(url);
    return res.data;
  };
  const mutationResult = useMutation(
    (args: GetDocsArgs) =>
      getPersonDocuments(
        args.personUuid,
        args.document_type,
        args.is_attachment
      ),
    {}
  );
  return {
    ...mutationResult,
  };
}

export interface DeleteDocsType {
  personUuid: string;
  document_type: string;
  filename: string;
}

export function useDeletePersonDocument() {
  const { requestApi } = useAppContext();
  const deletePersonDocument = async (
    personUuid: string,
    document_type: string,
    filename: string
  ) => {
    await requestApi.delete(
      `${API_URL}/person/${personUuid}/documents?person_uuid=${personUuid}&document_type=${document_type}&filename=${encodeURIComponent(
        filename
      )}`
    );
  };
  const mutationResult = useMutation(
    (args: DeleteDocsType) =>
      deletePersonDocument(args.personUuid, args.document_type, args.filename),
    {}
  );
  return { ...mutationResult };
}

export function useGetItemDocuments(
  itemUuid: string,
  is_attachment?: boolean,
  options?: { enabled?: boolean }
) {
  const { requestApi } = useAppContext();

  const getItemDocuments = async (): Promise<ItemDocument[]> => {
    if (itemUuid) {
      let url = `${API_URL}/item/${itemUuid}/documents`;
      if (is_attachment) {
        url = `${url}?is_attachment=true`;
      }
      const res = await requestApi.get(url);
      return res.data;
    }
    return [];
  };

  const queryResult = useQuery(
    ["itemDocuments", itemUuid, is_attachment],
    getItemDocuments,
    {
      enabled: !!itemUuid && (options?.enabled ?? true),
      staleTime: 30000,
    }
  );

  return {
    ...queryResult,
  };
}

export function useDeleteItemDocument() {
  const { requestApi } = useAppContext();

  const deleteItemDocument = async ({
    itemUuid,
    filename,
  }: {
    itemUuid: string;
    filename: string;
  }): Promise<void> => {
    await requestApi.delete(
      `${API_URL}/item/${itemUuid}/documents?filename=${encodeURIComponent(
        filename
      )}`
    );
  };

  const mutationResult = useMutation(deleteItemDocument, {});

  return {
    ...mutationResult,
  };
}

export function useDeleteAllItemDocuments() {
  const { requestApi } = useAppContext();

  const deleteAllItemDocuments = async (itemUuid: string): Promise<void> => {
    await requestApi.delete(`${API_URL}/item/${itemUuid}/documents/all`);
  };

  const mutationResult = useMutation(deleteAllItemDocuments, {});

  return {
    ...mutationResult,
  };
}

export function useExtractDocumentInfo() {
  const { requestApi } = useAppContext();

  const extractDocumentInfo = async (
    input: ExtractDocumentInfoRequest
  ): Promise<any> => {
    const { person_uuid, item_type, filename } = input;
    const params = new URLSearchParams();
    params.append("person_uuid", person_uuid);
    params.append("item_type", item_type);
    params.append("filename", filename);
    const res = await requestApi.post(
      `${API_URL}/person/${person_uuid}/documents/extract?${params.toString()}`
    );
    return res.data;
  };

  const mutationResult = useMutation(
    (input: ExtractDocumentInfoRequest) => extractDocumentInfo(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetItemDocumentsMutation() {
  const { requestApi } = useAppContext();

  const getItemDocuments = async (
    itemUuid: string
  ): Promise<ItemDocument[]> => {
    if (itemUuid) {
      const res = await requestApi.get(
        `${API_URL}/item/${itemUuid}/documents?is_attachment=true`
      );
      return res.data;
    }
    return [];
  };

  const mutationResult = useMutation(getItemDocuments, {});

  return {
    ...mutationResult,
  };
}

export interface CloneReportDocumentsArgs {
  reportUuid: string;
  body: CloneReportDocumentsRequest;
}

export function useCloneReportDocuments() {
  const { requestApi } = useAppContext();

  const cloneReportDocuments = async ({
    reportUuid,
    body,
  }: CloneReportDocumentsArgs): Promise<unknown> => {
    const res = await requestApi.post(
      `${API_URL}/360/reports/${reportUuid}/documents/clone`,
      body
    );
    return res.data;
  };

  const mutationResult = useMutation(
    (args: CloneReportDocumentsArgs) => cloneReportDocuments(args),
    {}
  );

  return {
    ...mutationResult,
  };
}
